import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { CreditCard, ClipboardList, CalendarRange, User, Edit2, Save, X } from "lucide-react";
import { supabase } from "../../supabaseClient";
import ImageUpload from "../../components/ImageUpload";

const MONTH_OPTIONS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

const monthLabel = (value) => MONTH_OPTIONS.find((m) => Number(m.value) === Number(value))?.label || "N/A";
const AGE_OPTIONS = Array.from({ length: 15 }, (_, i) => String(i + 4)); // 4-18
const GRADE_OPTIONS = Array.from({ length: 12 }, (_, i) => String(i + 1)); // 1-12

const normalizeArrayField = (value) => {
  if (Array.isArray(value)) return value.map((v) => String(v));
  if (typeof value === "string") {
    const trimmed = value.replace(/[{}]/g, "");
    return trimmed
      .split(",")
      .map((v) => v.trim())
      .filter(Boolean);
  }
  return value;
};

const normalizeProgram = (program) => ({
  ...program,
  registration_format: program?.registration_format || "age",
  in_season_start_month: program?.in_season_start_month || 11,
  in_season_end_month: program?.in_season_end_month || 3,
  off_season_start_month: program?.off_season_start_month || 4,
  off_season_end_month: program?.off_season_end_month || 10,
  age_groups: normalizeArrayField(program?.age_groups),
  boys_ages_offered: normalizeArrayField(program?.boys_ages_offered),
  girls_ages_offered: normalizeArrayField(program?.girls_ages_offered),
});

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

const formatCurrency = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  const numeric = Number(value);
  if (Number.isNaN(numeric)) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(numeric);
};

export default function AdminProfile({ userId }) {
  const navigate = useNavigate();
  const [programs, setPrograms] = useState([]);
  const [adminDisplayName, setAdminDisplayName] = useState("");
  const [followedSports, setFollowedSports] = useState([]);
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [formData, setFormData] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [locationQuery, setLocationQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingLatLng, setLoadingLatLng] = useState(false);
  const [activeTab, setActiveTab] = useState("profile");

  const [purchaseLoading, setPurchaseLoading] = useState(false);
  const [purchaseError, setPurchaseError] = useState(null);
  const [currentPurchase, setCurrentPurchase] = useState(null);
  const [adoptionStats, setAdoptionStats] = useState({
    rostered: 0,
    active: 0,
    rate: 0,
    loading: false,
  });

  const upgradeProductId = process.env.REACT_APP_STRIPE_PROGRAM_PRODUCT_ID || "prod_U7E6APAzKhFQhJ";

  const bucketLabel = useMemo(() => {
    const isGradeFormat = (formData?.registration_format || "age") === "grade";
    return isGradeFormat ? "Grade Groups" : "Age Groups";
  }, [formData?.registration_format]);

  const boysLabel = useMemo(() => {
    const isGradeFormat = (formData?.registration_format || "age") === "grade";
    return isGradeFormat ? "Boys Grades" : "Boys Ages";
  }, [formData?.registration_format]);

  const girlsLabel = useMemo(() => {
    const isGradeFormat = (formData?.registration_format || "age") === "grade";
    return isGradeFormat ? "Girls Grades" : "Girls Ages";
  }, [formData?.registration_format]);

  const formatLabel = useMemo(() => {
    const isGradeFormat = (formData?.registration_format || "age") === "grade";
    return isGradeFormat ? "Grade-based" : "Age-based";
  }, [formData?.registration_format]);

  useEffect(() => {
    const fetchData = async () => {
      if (!userId) return;
      setLoading(true);

      try {
        const { data: userSportsData, error: userSportsError } = await supabase
          .from("user_sport_info")
          .select("sport")
          .eq("user_id", userId);

        if (!userSportsError && userSportsData?.length > 0) {
          const sportIds = userSportsData.map((row) => row.sport);
          const { data: sportsList, error: sportsListError } = await supabase
            .from("sports")
            .select("id, name")
            .in("id", sportIds);

          if (!sportsListError) {
            setFollowedSports(sportsList || []);
          }
        }
      } catch (err) {
        console.error("Error fetching sports:", err);
      }

      const { data, error } = await supabase
        .from("programs")
        .select("*")
        .eq("administrator", userId);

      if (error) {
        console.error("Error fetching programs:", error);
      } else {
        const programsWithUrls = (data || []).map((p) => {
          let displayLogo = p.program_logo;
          if (displayLogo && !displayLogo.startsWith("http")) {
            const { data: urlData } = supabase.storage
              .from("program_logos")
              .getPublicUrl(displayLogo);
            displayLogo = urlData.publicUrl;
          }
          return { ...p, display_logo_url: displayLogo };
        });

        setPrograms(programsWithUrls);
        const initial = programsWithUrls[0] || null;
        if (initial) {
          const normalizedInitial = normalizeProgram(initial);
          setSelectedProgram(initial);
          setFormData(normalizedInitial);
          setLocationQuery(normalizedInitial.location_address || "");
          setActiveTab("profile");
        }
      }
      setLoading(false);
    };

    fetchData();
  }, [userId]);

  useEffect(() => {
    const resolveAdminName = async () => {
      const adminId = formData?.administrator;
      if (!adminId) {
        setAdminDisplayName("");
        return;
      }

      const { data: adminRow } = await supabase
        .from("users")
        .select("full_name")
        .eq("id", adminId)
        .maybeSingle();

      if (adminRow?.full_name) {
        setAdminDisplayName(adminRow.full_name);
        return;
      }

      const { data: authData } = await supabase.auth.getUser();
      const authUser = authData?.user;
      if (authUser?.id === adminId) {
        const metaName = authUser.user_metadata?.full_name || authUser.user_metadata?.name || "";
        setAdminDisplayName(metaName || "");
        return;
      }

      setAdminDisplayName("");
    };

    resolveAdminName();
  }, [formData?.administrator]);

  const fetchCurrentPurchase = async () => {
    if (!selectedProgram?.id) return;
    setPurchaseLoading(true);
    setPurchaseError(null);

    const today = new Date().toISOString().slice(0, 10);
    const { data, error } = await supabase
      .from("program_purchases")
      .select("*")
      .eq("program_id", selectedProgram.id)
      .lte("start_date", today)
      .gte("end_date", today)
      .order("end_date", { ascending: false })
      .limit(1);

    if (error) {
      setPurchaseError(error.message);
      setCurrentPurchase(null);
    } else {
      const active = (data || []).find((row) => !row.status || row.status === "active") || null;
      setCurrentPurchase(active);
    }

    setPurchaseLoading(false);
  };

  useEffect(() => {
    fetchCurrentPurchase();
  }, [selectedProgram?.id]);

  useEffect(() => {
    const fetchAdoptionStats = async () => {
      if (!selectedProgram?.id) return;
      setAdoptionStats((prev) => ({ ...prev, loading: true }));

      try {
        const { data: authData } = await supabase.auth.getSession();
        const accessToken = authData?.session?.access_token;

        const { data, error } = await supabase.functions.invoke(
          "program-adoption-stats",
          {
            body: { programId: selectedProgram.id },
            headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
          }
        );

        if (error) {
          console.error("Adoption stats function error:", error);
          throw error;
        }

        setAdoptionStats({
          rostered: data?.rostered || 0,
          active: data?.active || 0,
          rate: data?.rate || 0,
          loading: false,
        });
      } catch (err) {
        console.error("Adoption stats error:", err);
        setAdoptionStats((prev) => ({ ...prev, loading: false }));
      }
    };

    fetchAdoptionStats();
  }, [selectedProgram?.id]);

  const handleProgramChange = (programId) => {
    const program = programs.find((p) => p.id === programId);
    setSelectedProgram(program);
    setFormData(normalizeProgram(program));
    setLocationQuery(program.location_address || "");
    setIsEditing(false);
    setActiveTab("profile");
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const handleArrayChange = (field, value) => {
    const arrayValue = value.split(",").map((v) => v.trim()).filter(Boolean);
    setFormData({ ...formData, [field]: arrayValue });
  };

  const handleToggleArrayValue = (field, value) => {
    const current = Array.isArray(formData?.[field]) ? formData[field] : [];
    const exists = current.includes(value);
    const next = exists ? current.filter((v) => v !== value) : [...current, value];
    setFormData({ ...formData, [field]: next });
  };

  const fetchLatLng = async () => {
    if (!locationQuery) return;
    setLoadingLatLng(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(locationQuery)}`
      );
      const data = await response.json();
      if (data?.length > 0) {
        const { lat, lon } = data[0];
        setFormData((prev) => ({
          ...prev,
          location_address: locationQuery,
          latitude: parseFloat(lat),
          longitude: parseFloat(lon),
        }));
      } else {
        alert("Location not found!");
      }
    } catch (error) {
      console.error("Error fetching location:", error);
      alert("Failed to fetch location");
    }
    setLoadingLatLng(false);
  };

  const saveProfile = async () => {
    try {
      const { display_logo_url, ...updatePayload } = formData;
      if (formData.latitude && formData.longitude) {
        updatePayload.location = `POINT(${formData.longitude} ${formData.latitude})`;
      }

      const { error } = await supabase
        .from("programs")
        .update(updatePayload)
        .eq("id", formData.id);

      if (error) throw error;

      setPrograms((prev) => prev.map((p) => (p.id === formData.id ? formData : p)));
      setSelectedProgram(formData);
      alert("Profile saved successfully!");
      setIsEditing(false);
    } catch (error) {
      console.error("Save error:", error);
      alert("Failed to save profile");
    }
  };

  const handleCancel = () => {
    if (!selectedProgram) return;
    const reset = normalizeProgram(selectedProgram);
    setFormData(reset);
    setLocationQuery(reset.location_address || "");
    setIsEditing(false);
  };

  const handleUpgradeClick = () => {
    if (!selectedProgram?.id) return;
    const params = upgradeProductId ? `?productId=${encodeURIComponent(upgradeProductId)}` : "";
    navigate(`/admin/program/${selectedProgram.id}/upgrade${params}`);
  };

  if (loading) return <div className="p-10 text-center text-gray-500">Loading Nuvio Sport data...</div>;
  if (!selectedProgram || !formData) return <div className="p-10 text-center text-gray-500">No programs found.</div>;

  return (
    <div className="max-w-6xl mx-auto p-4 pb-24 md:pb-8 font-sans text-slate-800 relative">
      <div className="flex flex-col md:flex-row items-center gap-6 mb-8 bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
        <div className="flex-shrink-0">
          <ImageUpload
            bucket="program_logos"
            shape="rounded-2xl"
            height="h-28"
            width="w-28"
            currentImage={formData.display_logo_url || formData.program_logo}
            onUploadComplete={async ({ path, fullUrl }) => {
              const { data, error } = await supabase
                .from("programs")
                .update({ program_logo: path })
                .eq("id", formData.id)
                .select();

              if (!error && data.length > 0) {
                setFormData((prev) => ({ ...prev, program_logo: path, display_logo_url: fullUrl }));
              }
            }}
          />
        </div>

        <div className="flex-1 text-center md:text-left">
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900">{formData.name || "Unnamed Program"}</h1>
          <p className="text-blue-600 font-semibold flex items-center justify-center md:justify-start gap-2">
            {formData.sport || "Sport"} <span className="text-slate-300">|</span>
            <span className="text-slate-500 text-sm font-medium">{formData.program_type || "Program"}</span>
          </p>

          <div className="mt-4 flex flex-wrap justify-center md:justify-start gap-2">
            {isEditing ? (
              <>
                <button
                  onClick={saveProfile}
                  className="flex items-center gap-2 px-5 py-2 bg-blue-600 text-white text-sm font-bold rounded-lg hover:bg-blue-700 transition"
                >
                  <Save size={16} /> Save Changes
                </button>
                <button
                  onClick={handleCancel}
                  className="flex items-center gap-2 px-5 py-2 bg-slate-100 text-slate-700 text-sm font-bold rounded-lg hover:bg-slate-200 transition"
                >
                  <X size={16} /> Cancel
                </button>
              </>
            ) : (
              <button
                onClick={() => setIsEditing(true)}
                className="flex items-center gap-2 px-5 py-2 bg-white border border-slate-200 text-slate-700 text-sm font-bold rounded-lg shadow-sm hover:bg-slate-50 transition"
              >
                <Edit2 size={16} /> Edit Profile
              </button>
            )}
          </div>
        </div>

        {programs.length > 1 && (
          <div className="w-full md:w-56">
            <select
              className="w-full bg-gray-50 border border-gray-300 text-gray-900 text-sm rounded-lg focus:ring-blue-500 focus:border-blue-500 block p-2.5"
              value={selectedProgram.id}
              onChange={(e) => handleProgramChange(e.target.value)}
            >
              {programs.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>

      <div className="flex flex-col md:flex-row gap-8">
        <div className="w-full md:w-64 flex-shrink-0">
          <nav className="flex md:flex-col overflow-x-auto md:overflow-visible gap-2 p-1.5 bg-slate-100/50 rounded-2xl md:bg-transparent">
            {[
              { id: "profile", label: "Program Profile", icon: <User size={18} /> },
              { id: "details", label: "Eligibility", icon: <ClipboardList size={18} /> },
              { id: "seasons", label: "Seasons & Location", icon: <CalendarRange size={18} /> },
              { id: "billing", label: "Billing", icon: <CreditCard size={18} /> },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center justify-between w-full px-4 py-3.5 rounded-xl text-sm font-bold transition-all whitespace-nowrap
                ${activeTab === tab.id
                    ? "bg-blue-600 text-white shadow-lg shadow-blue-200 translate-x-1"
                    : "text-slate-500 hover:bg-white hover:text-slate-900"}`}
              >
                <div className="flex items-center gap-3">
                  {tab.icon}
                  {tab.label}
                </div>
              </button>
            ))}
          </nav>
        </div>

        <div className="flex-1 min-w-0">
          {activeTab === "profile" && (
            <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
              <div className="bg-white p-6 shadow-sm rounded-2xl border border-slate-100">
                <h2 className="text-lg font-bold mb-6 text-slate-900 border-b pb-2 flex items-center gap-2">
                  <User size={20} className="text-blue-500" /> Program Overview
                </h2>

                <div className="space-y-5">
                  <div>
                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Program Name</label>
                    {isEditing ? (
                      <input
                        className="w-full bg-slate-50 border border-slate-200 text-sm rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 outline-none"
                        value={formData.name || ""}
                        onChange={(e) => handleChange("name", e.target.value)}
                      />
                    ) : (
                      <p className="text-sm font-semibold text-slate-800">{formData.name || "Unnamed Program"}</p>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-400 uppercase tracking-wider mb-2">Description</label>
                    {isEditing ? (
                      <textarea
                        rows={4}
                        className="w-full bg-slate-50 border border-slate-200 text-sm rounded-lg p-2.5 focus:ring-2 focus:ring-blue-500 outline-none resize-none"
                        value={formData.description || ""}
                        onChange={(e) => handleChange("description", e.target.value)}
                      />
                    ) : (
                      <p className="text-slate-600 text-sm leading-relaxed">{formData.description || "No description provided."}</p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                    <div className="flex flex-col">
                      <span className="text-xs font-semibold text-slate-500 uppercase mb-1">Sport</span>
                      <div className="relative">
                        <select
                          disabled={!isEditing}
                          className={`w-full p-2 rounded-md text-sm appearance-none outline-none transition-all ${
                            isEditing
                              ? "bg-white border border-gray-300 focus:ring-2 focus:ring-blue-500"
                              : "bg-gray-100 border border-transparent text-gray-700 cursor-not-allowed"
                          }`}
                          value={formData.sport || ""}
                          onChange={(e) => handleChange("sport", e.target.value)}
                        >
                          <option value="" disabled>Select Sport</option>
                          {formData.sport && !followedSports.some((s) => s.name === formData.sport) && (
                            <option value={formData.sport}>{formData.sport}</option>
                          )}
                          {followedSports.map((sport) => (
                            <option key={sport.id} value={sport.name}>{sport.name}</option>
                          ))}
                        </select>
                        {isEditing && (
                          <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-500">
                            <svg className="fill-current h-4 w-4" viewBox="0 0 20 20"><path d="M9.293 12.95l.707.707L15.657 8l-1.414-1.414L10 10.828 5.757 6.586 4.343 8z" /></svg>
                          </div>
                        )}
                      </div>
                    </div>

                    <Field label="Program Type" value={formData.program_type} isEditing={isEditing} onChange={(e) => handleChange("program_type", e.target.value)} />

                    <div className="flex flex-col">
                      <span className="text-xs font-semibold text-slate-500 uppercase mb-1">Website</span>
                      {isEditing ? (
                        <input
                          className="w-full p-2 border border-gray-300 rounded-md text-sm"
                          value={formData.website_url || ""}
                          onChange={(e) => handleChange("website_url", e.target.value)}
                        />
                      ) : (
                        formData.website_url ? (
                          <a
                            href={`https://${formData.website_url.replace(/^https?:\/\//, '')}`}
                            target="_blank"
                            rel="noreferrer"
                            className="text-blue-600 hover:underline truncate"
                          >
                            {formData.website_url}
                          </a>
                        ) : <span className="text-gray-400 text-sm">N/A</span>
                      )}
                    </div>

                    <Field label="Instagram" value={formData.ig_handle} isEditing={isEditing} onChange={(e) => handleChange("ig_handle", e.target.value)} />
                    <Field label="Administrator" value={adminDisplayName || "Name unavailable"} isEditing={false} />
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === "details" && (
            <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
              <div className="bg-white p-6 shadow-sm rounded-2xl border border-slate-100">
                <h2 className="text-lg font-bold mb-6 text-slate-900 border-b pb-2 flex items-center gap-2">
                  <ClipboardList size={20} className="text-blue-500" /> Registration & Eligibility
                </h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                  <Field
                    label="Registration Format"
                    value={formatLabel}
                    isEditing={isEditing}
                    onChange={(e) => handleChange("registration_format", e.target.value)}
                    asSelect
                    options={[
                      { value: "age", label: "Age-based" },
                      { value: "grade", label: "Grade-based" },
                    ]}
                  />
                </div>

                <div className="space-y-4">
                  <ArrayField
                    label={bucketLabel}
                    value={formData.age_groups}
                    isEditing={isEditing}
                    onChange={(e) => handleArrayChange("age_groups", e.target.value)}
                    options={(formData?.registration_format || "age") === "grade" ? GRADE_OPTIONS : AGE_OPTIONS}
                    onToggle={(value) => handleToggleArrayValue("age_groups", value)}
                  />
                  <ArrayField
                    label={boysLabel}
                    value={formData.boys_ages_offered}
                    isEditing={isEditing}
                    onChange={(e) => handleArrayChange("boys_ages_offered", e.target.value)}
                    options={(formData?.registration_format || "age") === "grade" ? GRADE_OPTIONS : AGE_OPTIONS}
                    onToggle={(value) => handleToggleArrayValue("boys_ages_offered", value)}
                  />
                  <ArrayField
                    label={girlsLabel}
                    value={formData.girls_ages_offered}
                    isEditing={isEditing}
                    onChange={(e) => handleArrayChange("girls_ages_offered", e.target.value)}
                    options={(formData?.registration_format || "age") === "grade" ? GRADE_OPTIONS : AGE_OPTIONS}
                    onToggle={(value) => handleToggleArrayValue("girls_ages_offered", value)}
                  />
                </div>
              </div>
            </div>
          )}

          {activeTab === "seasons" && (
            <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
              <div className="bg-white p-6 shadow-sm rounded-2xl border border-slate-100">
                <h2 className="text-lg font-bold mb-6 text-slate-900 border-b pb-2 flex items-center gap-2">
                  <CalendarRange size={20} className="text-blue-500" /> Seasons & Location
                </h2>

                <div className="bg-blue-50 p-5 rounded-xl border border-blue-100 mb-6">
                  <label className="block text-xs font-bold text-blue-800 uppercase mb-3">Location & Map Data</label>
                  {isEditing ? (
                    <div className="flex gap-2 mb-3">
                      <input
                        className="flex-1 p-2.5 border border-blue-200 rounded-lg text-sm"
                        placeholder="Enter address..."
                        value={locationQuery}
                        onChange={(e) => setLocationQuery(e.target.value)}
                      />
                      <button
                        onClick={fetchLatLng}
                        disabled={loadingLatLng}
                        className="bg-blue-600 text-white px-4 py-2 rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50"
                      >
                        {loadingLatLng ? "..." : "Fetch"}
                      </button>
                    </div>
                  ) : (
                    <p className="text-gray-900 font-medium mb-2">{formData.location_address || "No address set"}</p>
                  )}
                  <div className="flex gap-4 text-xs text-blue-600 font-mono">
                    <span className="bg-blue-100 px-2 py-1 rounded">Lat: {formData.latitude || "N/A"}</span>
                    <span className="bg-blue-100 px-2 py-1 rounded">Lon: {formData.longitude || "N/A"}</span>
                  </div>
                </div>

                <div className="bg-amber-50 p-5 rounded-xl border border-amber-100">
                  <label className="block text-xs font-bold text-amber-800 uppercase mb-3">Season Windows</label>
                  {isEditing ? (
                    <div className="grid sm:grid-cols-2 gap-4">
                      <div className="bg-white p-4 rounded-xl border border-amber-100">
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">In-Season</p>
                        <div className="flex items-center gap-2">
                          <select
                            value={formData.in_season_start_month || 11}
                            onChange={(e) => handleChange("in_season_start_month", Number(e.target.value))}
                            className="flex-1 p-2 border border-gray-300 rounded-md text-sm bg-white"
                          >
                            {MONTH_OPTIONS.map((m) => (
                              <option key={`profile-in-start-${m.value}`} value={m.value}>{m.label}</option>
                            ))}
                          </select>
                          <span className="text-gray-400 font-bold">-</span>
                          <select
                            value={formData.in_season_end_month || 3}
                            onChange={(e) => handleChange("in_season_end_month", Number(e.target.value))}
                            className="flex-1 p-2 border border-gray-300 rounded-md text-sm bg-white"
                          >
                            {MONTH_OPTIONS.map((m) => (
                              <option key={`profile-in-end-${m.value}`} value={m.value}>{m.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div className="bg-white p-4 rounded-xl border border-amber-100">
                        <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-2">Off-Season</p>
                        <div className="flex items-center gap-2">
                          <select
                            value={formData.off_season_start_month || 4}
                            onChange={(e) => handleChange("off_season_start_month", Number(e.target.value))}
                            className="flex-1 p-2 border border-gray-300 rounded-md text-sm bg-white"
                          >
                            {MONTH_OPTIONS.map((m) => (
                              <option key={`profile-off-start-${m.value}`} value={m.value}>{m.label}</option>
                            ))}
                          </select>
                          <span className="text-gray-400 font-bold">-</span>
                          <select
                            value={formData.off_season_end_month || 10}
                            onChange={(e) => handleChange("off_season_end_month", Number(e.target.value))}
                            className="flex-1 p-2 border border-gray-300 rounded-md text-sm bg-white"
                          >
                            {MONTH_OPTIONS.map((m) => (
                              <option key={`profile-off-end-${m.value}`} value={m.value}>{m.label}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="grid sm:grid-cols-2 gap-3">
                      <div className="bg-white p-3 rounded-lg border border-amber-100">
                        <p className="text-xs text-gray-500 uppercase font-semibold">In-Season</p>
                        <p className="text-sm font-bold text-gray-800">
                          {monthLabel(formData.in_season_start_month)} - {monthLabel(formData.in_season_end_month)}
                        </p>
                      </div>
                      <div className="bg-white p-3 rounded-lg border border-amber-100">
                        <p className="text-xs text-gray-500 uppercase font-semibold">Off-Season</p>
                        <p className="text-sm font-bold text-gray-800">
                          {monthLabel(formData.off_season_start_month)} - {monthLabel(formData.off_season_end_month)}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {activeTab === "billing" && (
            <div className="space-y-6 animate-in fade-in slide-in-from-right-4 duration-300">
              <div className="bg-white p-6 shadow-sm rounded-2xl border border-slate-100">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
                  <h2 className="text-lg font-bold text-slate-900 border-b sm:border-b-0 pb-2 sm:pb-0 flex items-center gap-2">
                    <CreditCard size={20} className="text-blue-500" /> Billing
                  </h2>
                  <div className="flex flex-wrap gap-2">
                    <button
                      onClick={fetchCurrentPurchase}
                      className="px-4 py-2 bg-white border border-slate-200 text-slate-700 text-sm font-bold rounded-lg hover:bg-slate-50 transition"
                    >
                      Refresh Billing
                    </button>
                    <button
                      onClick={handleUpgradeClick}
                      className="px-4 py-2 bg-blue-600 text-white text-sm font-bold rounded-lg hover:bg-blue-700 transition"
                    >
                      Upgrade
                    </button>
                  </div>
                </div>

                {purchaseLoading && (
                  <div className="text-sm text-slate-500">Loading billing status...</div>
                )}

                {purchaseError && (
                  <div className="text-sm text-red-600">{purchaseError}</div>
                )}

                {!purchaseLoading && !purchaseError && !currentPurchase && (
                  <div className="bg-slate-50 border border-slate-200 rounded-xl p-5">
                    <p className="text-sm text-slate-600">Current Plan</p>
                    <p className="text-2xl font-bold text-slate-900 mt-1">Free</p>
                    <p className="text-xs text-slate-500 mt-2">No active billing records found for this program.</p>
                  </div>
                )}

                {!purchaseLoading && !purchaseError && currentPurchase && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <BillingStat label="Tier" value={currentPurchase.tier || "—"} />
                    <BillingStat label="Roster Limit" value={currentPurchase.roster_limit || "—"} />
                    <BillingStat label="Price Paid" value={formatCurrency(currentPurchase.price_paid)} />
                    <BillingStat label="Status" value={currentPurchase.status || "active"} />
                    <BillingStat label="Season Year" value={currentPurchase.season_year || "—"} />
                    <BillingStat label="Season Phase" value={currentPurchase.season_phase || "—"} />
                    <BillingStat label="Start Date" value={formatDate(currentPurchase.start_date)} />
                    <BillingStat label="End Date" value={formatDate(currentPurchase.end_date)} />
                  </div>
                )}

              </div>
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

const Field = ({ label, value, isEditing, onChange, asSelect = false, options = [] }) => (
  <div className="flex flex-col">
    <span className="text-xs font-semibold text-slate-500 uppercase mb-1">{label}</span>
    {isEditing && onChange ? (
      asSelect ? (
        <select
          className="w-full p-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 outline-none bg-white"
          value={value === "Grade-based" ? "grade" : value === "Age-based" ? "age" : (value || "")}
          onChange={onChange}
        >
          {options.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      ) : (
        <input className="w-full p-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 outline-none" value={value || ""} onChange={onChange} />
      )
    ) : (
      <span className="text-slate-900 text-sm font-medium truncate">{value || "N/A"}</span>
    )}
  </div>
);

const ArrayField = ({ label, value, isEditing, onChange, options = [], onToggle, placeholder = "Comma separated" }) => (
  <div className="flex flex-col sm:flex-row sm:items-center gap-4 border-b border-gray-100 pb-3 last:border-0">
    <span className="text-sm font-semibold text-gray-500 w-32 uppercase tracking-wide text-xs pt-1">{label}</span>
    {isEditing ? (
      options.length > 0 ? (
        <div className="flex-1 flex flex-wrap gap-2">
          {options.map((opt) => {
            const selected = Array.isArray(value) && value.includes(opt);
            return (
              <button
                key={`${label}-${opt}`}
                type="button"
                onClick={() => onToggle && onToggle(opt)}
                className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                  selected
                    ? "bg-blue-600 text-white border-blue-600 shadow-sm"
                    : "bg-white text-gray-600 border-gray-200 hover:border-blue-300 hover:text-blue-600"
                }`}
              >
                {opt}
              </button>
            );
          })}
        </div>
      ) : (
        <input
          className="flex-1 p-2 border border-gray-300 rounded-md text-sm focus:ring-2 focus:ring-blue-500 outline-none"
          value={value?.join(", ") || ""}
          onChange={onChange}
          placeholder={placeholder}
        />
      )
    ) : (
      <div className="flex flex-wrap gap-2">
        {value && value.length > 0 ? value.map((v, i) => (
          <span key={i} className="bg-gray-100 text-gray-700 text-xs px-2.5 py-1 rounded-full font-medium border border-gray-200">{v}</span>
        )) : <span className="text-gray-400 text-sm italic">None</span>}
      </div>
    )}
  </div>
);

const BillingStat = ({ label, value }) => (
  <div className="bg-slate-50 border border-slate-200 rounded-xl p-4">
    <p className="text-xs text-slate-500 uppercase font-semibold">{label}</p>
    <p className="text-sm font-bold text-slate-900 mt-1">{value}</p>
  </div>
);
